// @ts-nocheck
import React from "react";
import { useState, useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Printer,
  Play,
  Edit,
  MoreHorizontal,
  Calendar,
  StopCircle,
  Layers,
  Flame,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatNumber } from "@/lib/utils";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { LayoutGrid, CalendarDays, Sparkles, QrCode, Activity } from "lucide-react";
import CartonLabelPrintModal from "@/components/quality/print/CartonLabelPrintModal";
import { PrepressQueuePage } from "./PrepressQueuePage";
import { PrepressReadyPoolTab } from "./components/PrepressReadyPoolTab";
import { SegmentedViewSwitcher, type ViewOption } from "@/components/shared/SegmentedViewSwitcher";
import { toast } from "sonner";
import { BulkActionBar, BulkAction } from "@/components/shared/BulkActionBar";
import { trpcClient } from "@/lib/trpc";
import { mapProductionOrderListItem } from "@/lib/transformers/production-orders";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";
import { ProductionOrderStatus } from "@/config/status-registry";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import { useCustomerLookup } from "@/hooks/useFKLookup";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import type { FilterField } from "@/components/shared/FilterBar";
import {
  downloadExcelTemplate as downloadTemplate,
  type ExcelColumnDef,
} from "@/lib/excel.utils";
import { useExcelExport } from "@/hooks/useExcelExport";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { ResponsiveDataView } from "@/components/shared/ResponsiveDataView";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  useDatePeriodFilter,
  DatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { ProductionOrderPrintModal } from "@/components/planning/print/ProductionOrderPrintModal";
import { ProductionScheduleReportPrintModal } from "@/components/planning/print/ProductionScheduleReportPrintModal";
import { ProductionOrderKanban } from "./components/ProductionOrderKanban";
import { ProductionOrderMatrixGrid, DEFAULT_PROCESS_GROUPS, type POGroupByOption } from "./components/ProductionOrderMatrixGrid";
import { ProcessProgressBoard } from "./components/ProcessProgressBoard";
import { ProductionOrderKpiCards, type UrgencyFilterType } from "./components/ProductionOrderKpiCards";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useConfirm } from "@/providers/ConfirmProvider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { emsTabClass } from "@/components/ui/ems-tabs";

const CORE_PO_STATUS_KEYS = new Set([
  "DRAFT",
  "PENDING_APPROVAL",
  "PLANNED",
  "READY_FOR_SCHEDULING",
  "RELEASED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
]);

/** Nhãn ngắn trên hàng chip trạng thái (hàng phải vừa 1 dòng ở màn 1440px) */
const SHORT_STATUS_LABELS: Record<string, string> = {
  PENDING_APPROVAL: "Chờ duyệt",
};

const STATUS_ITEMS: StatusSidebarItem[] = Object.entries(
  ProductionOrderStatus,
).map(([key, cfg]) => ({
  key,
  label: SHORT_STATUS_LABELS[key] || cfg.label,
  color: cfg.color,
  dotColor: cfg.dotColor,
}));

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "lsxId", label: "Mã LSX" },
  { key: "productName", label: "Sản phẩm" },
  { key: "quantity", label: "Số lượng", type: "number" },
  { key: "status", label: "Trạng thái" },
  {
    key: "deadline",
    label: "Hạn trả hàng",
    type: "date",
    format: (val: any) => (val ? new Date(val).toLocaleDateString("vi-VN") : ""),
  },
  { key: "poReference", label: "PO Reference" },
];

const FILTER_FIELDS: FilterField[] = [
  { key: "orderId", label: "Mã LSX", type: "text", placeholder: "LSX-..." },
  {
    key: "productName",
    label: "Sản phẩm",
    type: "text",
    placeholder: "Tên SP...",
  },
  { key: "poReference", label: "Số PO", type: "text", placeholder: "PO-..." },
  {
    key: "customerName",
    label: "Khách hàng",
    type: "text",
    placeholder: "Tên KH...",
  },
  { key: "plannedStart", label: "Ngày bắt đầu", type: "date-range" },
  { key: "plannedEnd", label: "Ngày kết thúc", type: "date-range" },
  {
    key: "priority",
    label: "Ưu tiên",
    type: "combobox-multi",
    options: [
      { value: "HIGH", label: "Cao" },
      { value: "NORMAL", label: "Thường" },
      { value: "LOW", label: "Thấp" },
    ],
  },
];

type POViewType = "table" | "pool" | "ctp-queue" | "tracking";

const PO_VIEW_OPTIONS: ViewOption<POViewType>[] = [
  { id: "table", label: "Danh Sách Lệnh SX", icon: Layers },
  { id: "pool", label: "Chờ Tạo Lệnh (Bể Gom Bài)", icon: Sparkles },
  { id: "ctp-queue", label: "Hàng Chờ CTP", icon: Printer },
  { id: "tracking", label: "Tiến Độ CĐ", icon: Activity },
];

/** view=… trên URL → màn hình; menu Tiến Độ Công Đoạn mở view=tracking (link cũ tab=tracking vẫn mở đúng) */
function toPoView(view?: string, tab?: string): POViewType {
  if (view === "pool") return "pool";
  if (view === "ctp-queue" || view === "prepress-queue") return "ctp-queue";
  if (view === "tracking" || tab === "tracking") return "tracking";
  return "table";
}

export default function ProductionOrderListPage() {
  const navigate = useNavigate();
  const routerSearch = useRouterState({ select: (s) => s.location.search }) as Record<string, any>;
  const rawView = routerSearch?.view;
  const rawTab = routerSearch?.tab;
  const initialView: POViewType = toPoView(rawView, rawTab);

  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [printOrderIds, setPrintOrderIds] = useState<string[]>([]);
  const [cartonLabelOrderIds, setCartonLabelOrderIds] = useState<string[]>([]);
  const [showScheduleReportModal, setShowScheduleReportModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [activeView, setActiveView] = useState<POViewType>(initialView);

  useEffect(() => {
    if (rawView || rawTab) setActiveView(toPoView(rawView, rawTab));
  }, [rawView, rawTab]);

  const rawGroupBy = routerSearch?.groupBy as POGroupByOption | undefined;
  const [groupBy, setGroupBy] = useState<POGroupByOption>(rawGroupBy || "none");

  useEffect(() => {
    if (rawGroupBy) {
      setGroupBy(rawGroupBy);
    }
  }, [rawGroupBy]);

  const handleGroupByChange = (val: POGroupByOption) => {
    setGroupBy(val);
    navigate({
      search: (prev: any) => ({ ...prev, groupBy: val === "none" ? undefined : val }),
      replace: true,
    });
  };

  const handleViewChange = (v: POViewType) => {
    setActiveView(v);
    navigate({
      search: (prev: any) => ({ ...prev, view: v }),
      replace: true,
    });
  };
  const confirm = useConfirm();
  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter();
  const { hasPermission } = useRoleAccess();
  const queryClient = useQueryClient();

  const { options: customerOptions } = useCustomerLookup();

  const filterFields = useMemo<FilterField[]>(() => {
    return FILTER_FIELDS.map((f) => {
      if (f.key === "customerName") {
        return {
          key: "customerId",
          label: "Khách hàng",
          type: "combobox-multi",
          options: customerOptions.map((c) => ({ value: c.id, label: c.label })),
        } as FilterField;
      }
      return f;
    });
  }, [customerOptions]);
  const [dynamicGroups, setDynamicGroups] = useState<any[]>(DEFAULT_PROCESS_GROUPS);

  useEffect(() => {
    fetch("/api/technical/process-groups")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setDynamicGroups(data);
        }
      })
      .catch(() => {});
  }, []);

  const [urgencyFilter, setUrgencyFilter] = useState<UrgencyFilterType | null>(null);

  const {
    items: rawItems,
    paginatedItems: rawPaginated,
    isLoading,
    searchQuery,
    filterValues,
    serverParams,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    meta,
    statusCounts: serverStatusCounts,
    statusFilter,
    onStatusChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "production-orders",
    queryFn: (params) => trpcClient.productionOrders.list.query(params) as any,
    extraParams: {
      ...(dateParams.from
        ? { plannedEndFrom: dateParams.from, plannedEndTo: dateParams.to }
        : {}),
      ...(urgencyFilter ? { urgencyFilter } : {}),
    },
  });

  // Transform items after API fetch
  const allOrders = useMemo(
    () => rawItems.map(mapProductionOrderListItem),
    [rawItems],
  );
  const paginatedItems = useMemo(
    () => rawPaginated.map(mapProductionOrderListItem),
    [rawPaginated],
  );

  const urgencyCounts = (meta as any)?.urgencyCounts || {
    urgent: 0,
    high: 0,
    approaching: 0,
    overdue: 0,
  };

  const displayItems = paginatedItems;

  const rowIds = paginatedItems.map((o: any) => o.orderId);
  const rowSelection = useRowSelection(rowIds);
  const {
    selectedIds,
    selectionMode,
    clearSelection,
    selectedCount,
    handleRowSelect,
  } = rowSelection;
  const filtered = allOrders;

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_lenh_san_xuat",
    sheetName: "Lệnh Sản Xuất",
    columns: EXPORT_COLUMNS,
    queryFn: (params) => trpcClient.productionOrders.list.query(params),
    currentFilters: serverParams,
    transformData: (items) => (items || []).map(mapProductionOrderListItem),
    selectedIds,
    idKey: "orderId",
  });

  const handleImport = async (rows: Record<string, unknown>[]) => {
    let success = 0;
    const errors: string[] = [];
    for (const row of rows) {
      try {
        await trpcClient.productionOrders.create.mutate(row as any);
        success++;
      } catch (err: any) {
        errors.push(`${row.lsxId}: ${err?.message || "Lỗi"} `);
      }
    }
    if (success > 0) {
      await queryClient.invalidateQueries({ queryKey: ["production-orders"] });
    }
    if (errors.length > 0)
      toast.error(
        `${errors.length} dòng lỗi: ${errors.slice(0, 3).join("; ")}`,
      );
  };

  const getBulkActions = (): BulkAction[] => {
    const sel = allOrders.filter((o: any) => selectedIds.has(o.orderId));
    if (!sel.length) return [];
    const allPending = sel.every((o: any) =>
      ["pending", "PENDING", "DRAFT", "PENDING_APPROVAL"].includes(o.status),
    );
    // Chỉ lệnh đã lên kế hoạch / đã ban hành mới chuyển Đang SX được (nháp, chờ duyệt phải duyệt ban hành trước)
    const allReleased = sel.every((o: any) =>
      ["PLANNED", "RELEASED"].includes(String(o.status || "").toUpperCase()),
    );
    const allRunning = sel.every((o: any) =>
      ["running", "IN_PROGRESS"].includes(o.status),
    );
    const actions: BulkAction[] = [
      {
        label: "In Lệnh SX",
        icon: <Printer className="w-3.5 h-3.5" />,
        onClick: () => {
          setPrintOrderIds(Array.from(selectedIds) as string[]);
        },
      },
      {
        // Tem thùng số nhảy của các lô đã đóng gói & nhập kho thuộc LSX đã chọn
        label: "In tem thùng",
        icon: <QrCode className="w-3.5 h-3.5" />,
        onClick: () => setCartonLabelOrderIds(Array.from(selectedIds) as string[]),
      },
    ];

    if (hasPermission("productionOrders", "update")) {
      actions.push({
        label: "Bắt đầu chạy",
        icon: <Play className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            bulkMutationFn: (ids) => trpcClient.productionOrders.bulkUpdateStatus.mutate({ ids: ids as string[], status: "IN_PROGRESS" }),
            actionName: "bắt đầu chạy",
            itemType: "lệnh sản xuất",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({
                queryKey: ["production-orders"],
              });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn bắt đầu chạy ${selectedCount} lệnh sản xuất đã chọn?`,
          });
        },
        disabled: !allReleased,
      });
      actions.push({
        label: "Tạm dừng",
        icon: <StopCircle className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            bulkMutationFn: (ids) => trpcClient.productionOrders.bulkUpdateStatus.mutate({ ids: ids as string[], status: "PAUSED" }),
            actionName: "tạm dừng",
            itemType: "lệnh sản xuất",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({
                queryKey: ["production-orders"],
              });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn tạm dừng ${selectedCount} lệnh sản xuất đã chọn?`,
          });
        },
        variant: "outline",
        disabled: !allRunning,
      });
    }
    if (hasPermission("productionOrders", "delete")) {
      actions.push({
        label: "Xóa",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            bulkMutationFn: (ids) => trpcClient.productionOrders.bulkDelete.mutate({ ids: ids as string[] }),
            actionName: "xóa",
            itemType: "lệnh sản xuất",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({
                queryKey: ["production-orders"],
              });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} lệnh sản xuất đã chọn?`,
          });
        },
        variant: "destructive",
        disabled: !allPending,
      });
    }
    return actions;
  };

  const COLS = 7;

  const filterTags = React.useMemo(() => {
    const selectedArr = statusFilter
      ? Array.isArray(statusFilter)
        ? statusFilter
        : [statusFilter]
      : [];
    return STATUS_ITEMS.map((item) => {
      const sc = serverStatusCounts?.find(
        (s: any) =>
          s.status?.toString()?.toUpperCase() === item.key?.toUpperCase(),
      );
      return {
        ...item,
        count: sc ? sc.count : 0,
      };
    }).filter(
      (item) =>
        CORE_PO_STATUS_KEYS.has(item.key) ||
        selectedArr.some((k) => String(k).toUpperCase() === item.key.toUpperCase()),
    );
  }, [serverStatusCounts, statusFilter]);
  return (
    <>
      <ListPageShell
        trashModule="production-orders"
        tabs={
          <div className="flex items-center gap-3 shrink-0 flex-nowrap">
            <button
              type="button"
              onClick={() => handleViewChange("table")}
              className={emsTabClass(activeView === "table", { size: "sm" })}
            >
              <Layers className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
              <span>Danh Sách Lệnh SX</span>
            </button>

            <button
              type="button"
              onClick={() => handleViewChange("pool")}
              className={emsTabClass(activeView === "pool", { size: "sm" })}
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <span>Chờ Lập Lệnh</span>
            </button>

            <button
              type="button"
              onClick={() => handleViewChange("ctp-queue")}
              className={emsTabClass(activeView === "ctp-queue", { size: "sm" })}
            >
              <Printer className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <span>Hàng Chờ CTP</span>
            </button>

            <button
              type="button"
              onClick={() => handleViewChange("tracking")}
              data-testid="po-view-tracking"
              className={emsTabClass(activeView === "tracking", { size: "sm" })}
            >
              <Activity className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span>Tiến Độ CĐ</span>
            </button>
          </div>
        }
        topBanner={
          activeView === "table" || activeView === "tracking" ? (
            <div className="flex flex-col divide-y divide-slate-200/80 -mx-3 -my-1">
              {/* DÒNG 2: Bộ lọc Thời gian & Gom nhóm (Position 0 - Rule 12) + Trạng thái + 4 Pill Khẩn Cấp (Phải) */}
              <div className="erp-po-filter-row flex items-center justify-between gap-2 px-3 py-1 bg-white/90 overflow-x-auto hide-scrollbar flex-nowrap">
                <div className="date-filter-pos0 flex items-center gap-1.5 shrink-0 flex-nowrap [&_button]:!h-7">
                  <Button
                    type="button"
                    data-testid="btn-toggle-day-group"
                    variant={groupBy === "day" ? "default" : "outline"}
                    size="sm"
                    className={cn(
                      "h-7 px-2 text-xs gap-1 cursor-pointer shrink-0",
                      groupBy === "day"
                        ? "bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
                    )}
                    onClick={() => handleGroupByChange(groupBy === "day" ? "none" : "day")}
                    title="Bật/Tắt gom nhóm LSX theo ngày"
                  >
                    <Calendar className="w-3.5 h-3.5 shrink-0" />
                    <span>Theo ngày</span>
                  </Button>
                  <DatePeriodFilter
                    period={period}
                    setPeriod={setPeriod}
                    customFrom={customFrom}
                    setCustomFrom={setCustomFrom}
                    customTo={customTo}
                    setCustomTo={setCustomTo}
                  />
                  <div className="h-5 w-px bg-slate-200 shrink-0 mx-0.5" />
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                    <span className="shrink-0 font-medium text-slate-600">Nhóm:</span>
                    <Select value={groupBy} onValueChange={(val: any) => handleGroupByChange(val)}>
                      <SelectTrigger className="!h-7 w-[140px] text-xs bg-white shadow-2xs border-slate-300">
                        <SelectValue placeholder="Không nhóm" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Không nhóm</SelectItem>
                        <SelectItem value="day">Theo Ngày (Kế hoạch / Tạo)</SelectItem>
                        <SelectItem value="customer">Theo Khách hàng</SelectItem>
                        <SelectItem value="status">Theo Trạng thái</SelectItem>
                        <SelectItem value="priority">Theo Mức ưu tiên</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Trạng thái LSX: hơn 5 lựa chọn → danh sách thả xuống, đặt cùng hàng bộ lọc */}
                <div className="flex items-center shrink-0">
                  <PageFilterTags
                    items={filterTags}
                    selectedKeys={
                      statusFilter
                        ? Array.isArray(statusFilter)
                          ? statusFilter
                          : [statusFilter]
                        : []
                    }
                    onSelect={onStatusChange}
                    dropdownLabel="Trạng thái SX"
                  />
                </div>

                <div className="flex items-center shrink-0 ml-auto">
                  <ProductionOrderKpiCards
                    counts={urgencyCounts}
                    activeFilter={urgencyFilter}
                    onSelectFilter={(f) => {
                      setUrgencyFilter(f);
                      onPageChange(1);
                    }}
                    isLoading={isLoading}
                    className="w-auto"
                  />
                </div>
              </div>

            </div>
          ) : undefined
        }
        unitLabel={activeView === "pool" ? "Bài bình" : "LSX"}
        actions={
          <ExportImportButtons
            onExport={handleExport}
            exportLoading={isExporting}
            onDownloadTemplate={() =>
              downloadTemplate(EXPORT_COLUMNS, "lenh_san_xuat")
            }
          >
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs bg-white border-blue-200 text-blue-700 hover:bg-blue-50 shadow-xs shrink-0"
              onClick={() => setShowScheduleReportModal(true)}
            >
              <Printer size={12} className="mr-1 shrink-0 text-blue-600" />
              <span className="truncate" title="Báo cáo kế hoạch sản xuất (A4 ngang)">Báo Cáo KHSX</span>
            </Button>
            {hasPermission("productionOrders", "create") && (
              <Button
                size="sm"
                className="h-7 text-xs shrink-0"
                onClick={() => navigate({ to: "/production-orders/create" })}
              >
                <Plus size={14} className="mr-1 shrink-0" />
                <span className="truncate">Thêm LSX</span>
              </Button>
            )}
          </ExportImportButtons>
        }
        filterFields={filterFields}
        filterValues={filterValues}
        showFilter={showFilter}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      >
        {activeView === "pool" && (
          <div className="h-full bg-slate-50/50 overflow-y-auto">
            <PrepressReadyPoolTab />
          </div>
        )}

        {activeView === "table" && (
          <ProductionOrderMatrixGrid
            orders={displayItems}
            activeProcessGroups={dynamicGroups}
            isLoading={isLoading}
            selectedIds={selectedIds}
            onSelectRow={handleRowSelect}
            onSelectAll={rowSelection.toggleSelectAll}
            isAllSelected={rowSelection.isAllSelected}
            groupBy={groupBy}
          />
        )}

        {activeView === "tracking" && (
          <ProcessProgressBoard orders={displayItems} activeProcessGroups={dynamicGroups} isLoading={isLoading} />
        )}

        {activeView === "ctp-queue" && (
          <div className="h-full bg-slate-50/50 overflow-y-auto">
            <PrepressQueuePage />
          </div>
        )}
      </ListPageShell>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />

      <ProductionOrderPrintModal
        orderIds={printOrderIds}
        onClose={() => setPrintOrderIds([])}
      />
      <CartonLabelPrintModal
        open={cartonLabelOrderIds.length > 0}
        productionOrderIds={cartonLabelOrderIds}
        onClose={() => setCartonLabelOrderIds([])}
      />
      <ProductionScheduleReportPrintModal
        open={showScheduleReportModal}
        onClose={() => setShowScheduleReportModal(false)}
      />
    </>
  );
}
